export type KoreanPackMetadata = {
  name: string;
  description: string;
};

/**
 * Korean catalog copy keyed by the stable source-pack id. Registry hand packs
 * add a `hand-` prefix and generated matrix decks add a language-pair suffix;
 * {@link koreanPackMetadata} normalizes both forms.
 */
export const KOREAN_PACK_METADATA: Readonly<Record<string, KoreanPackMetadata>> = {
  "freq-core-30": {
    name: "핵심 빈도 어휘 · 30단어",
    description: "영어, 러시아어, 스페인어, 독일어의 자주 쓰는 단어 30개로 여러 언어를 빠르게 체험해 보세요.",
  },
  "en-ru-greetings-a1": {
    name: "인사와 예절",
    description: "첫 만남과 기본적인 소통에 필요한 표현입니다.",
  },
  "en-ru-numbers-a1": {
    name: "숫자 1~20",
    description: "기초적인 수 세기에 필요한 숫자 표현입니다.",
  },
  "en-ru-days-months-a1": {
    name: "요일과 월",
    description: "요일과 1년의 열두 달을 익힙니다.",
  },
  "en-ru-food-a1": {
    name: "음식과 음료",
    description: "메뉴와 장보기에서 자주 쓰는 단어입니다.",
  },
  "en-ru-travel-a2": {
    name: "여행과 공항",
    description: "이동, 대여, 출입국 심사에 필요한 어휘입니다.",
  },
  "en-ru-city-a2": {
    name: "도시와 길 찾기",
    description: "거리, 건물, 길을 묻는 표현을 익힙니다.",
  },
  "en-ru-emotions-a2": {
    name: "감정과 상태",
    description: "감정과 기분을 표현하는 단어입니다.",
  },
  "en-ru-business-b1": {
    name: "비즈니스와 회의",
    description: "회의, 협상, 업무 연락에 필요한 어휘입니다.",
  },
  "en-ru-finance-b2": {
    name: "금융",
    description: "투자, 보고, 시장에서 쓰는 용어입니다.",
  },
  "en-ru-it-b2": {
    name: "IT와 개발",
    description: "엔지니어링과 제품 개발의 기본 어휘입니다.",
  },
  "en-ru-ai-c1": {
    name: "AI와 머신러닝",
    description: "머신러닝과 신경망 관련 용어입니다.",
  },
  "en-ru-medicine-b2": {
    name: "의학 기초 어휘",
    description: "진료실에서 필요한 기본 용어입니다.",
  },
  "kids-en-ru-colors": {
    name: "어린이를 위한 색깔 (EN→RU)",
    description: "아이와 함께 밝고 재미있는 색깔 단어를 배워요.",
  },
  "kids-en-ru-numbers": {
    name: "숫자 1~10 (EN→RU)",
    description: "하나부터 열까지 첫 숫자를 익힙니다.",
  },
  "kids-en-ru-animals": {
    name: "어린이를 위한 동물 (EN→RU)",
    description: "반려동물과 야생동물 이름을 배웁니다.",
  },
  "kids-en-ru-family": {
    name: "가족 (EN→RU)",
    description: "가족과 가까운 사람을 나타내는 단어입니다.",
  },
  "kids-en-ru-body": {
    name: "신체 부위 (EN→RU)",
    description: "어린이를 위한 신체 부위 단어입니다.",
  },
  "es-ru-basic-a1": {
    name: "스페인어 기초",
    description: "여행에 필요한 인사와 기본 어휘입니다.",
  },
  "es-ru-food-a1": {
    name: "스페인어 음식",
    description: "스페인어로 음식과 음료를 익힙니다.",
  },
  "es-en-travel-a2": {
    name: "스페인어 여행 기초 (ES→EN)",
    description: "스페인어권 국가에서 자주 쓰는 표현입니다.",
  },
  "zh-ru-basic-a1": {
    name: "중국어 인사",
    description: "기본 한자와 인사 표현을 익힙니다.",
  },
  "zh-en-numbers-a1": {
    name: "중국어 숫자 1~10 (ZH→EN)",
    description: "하나부터 천까지 셀 때 쓰는 중국어 숫자입니다.",
  },
  "ja-en-basic-a1": {
    name: "일본어 필수 표현 (JA→EN)",
    description: "히라가나와 한자로 된 인사와 공손한 표현입니다.",
  },
  "de-ru-basic-a1": {
    name: "독일어 기초",
    description: "독일어 기본 표현과 일상 어휘입니다.",
  },
  "fr-ru-basic-a1": {
    name: "프랑스어 기초",
    description: "프랑스어 기본 표현과 일상 어휘입니다.",
  },
  "it-ru-basic-a1": {
    name: "이탈리아어 기초",
    description: "이탈리아어 기본 표현과 일상 어휘입니다.",
  },
  "basic-core": {
    name: "기초 매트릭스",
    description: "자주 쓰는 기초 표현, 대명사, 숫자, 시간, 가족, 감정, 간단한 동작을 익힙니다.",
  },
  "daily-travel": {
    name: "일상과 여행",
    description: "집, 음식, 쇼핑, 도시, 교통, 여행, 자연, 날씨 어휘를 익힙니다.",
  },
  "professional-core": {
    name: "전문 어휘 매트릭스",
    description: "비즈니스, 경영, 금융, 마케팅, 법률, 의학, IT, AI, 공학 어휘입니다.",
  },
  "academic-culture": {
    name: "학업, 과학과 문화",
    description: "교육, 역사, 철학, 예술, 스포츠, 종교, 과학 분야 어휘입니다.",
  },
  "core-b1": {
    name: "핵심 어휘 B1",
    description: "사회적 상황, 추상어, 관계, 담화 표현, 일상 동작을 다룹니다.",
  },
  "professional-c1": {
    name: "전문 어휘 C1",
    description: "전략, 협상, 분석, 금융, 법률, 제품을 위한 고급 비즈니스 어휘입니다.",
  },
  "academic-c1": {
    name: "학술 어휘 C1",
    description: "논증, 연구, 방법론, 인과관계, 근거, 비판적 사고를 다룹니다.",
  },
  "core-b2": {
    name: "핵심 어휘 B2",
    description: "추상적인 일상 어휘, 논증, 인과관계, 비교, 문제와 해결을 다룹니다.",
  },
  "social-culture-b2": {
    name: "사회와 문화 B2",
    description: "미디어, 규범, 가치, 정체성, 윤리, 공공기관, 문화 참여를 다룹니다.",
  },
  "science-c1": {
    name: "과학과 기술 C1",
    description: "방법론, 데이터, 모델, 측정, 확률, 시스템, 실험, 제약을 다룹니다.",
  },
  "communication-b2": {
    name: "의사소통 B2",
    description: "명확히 묻기, 동의와 반대, 바꿔 말하기, 요청, 추천, 피드백을 다룹니다.",
  },
  "life-skills-b2": {
    name: "실생활 B2",
    description: "건강, 돈, 주거, 서비스, 문서, 안전, 디지털 생활을 다룹니다.",
  },
  "advanced-discourse-c1": {
    name: "고급 담화 C1",
    description: "완곡 표현, 필자의 관점, 결론, 양보, 강조, 종합, 평가, 틀과 한계를 다룹니다.",
  },
} as const;

export function koreanPackMetadata(id: string | undefined): KoreanPackMetadata | undefined {
  if (!id) return undefined;
  const normalized = id.startsWith("hand-") ? id.slice(5) : id;
  const exact = KOREAN_PACK_METADATA[normalized];
  if (exact) return exact;
  if (!normalized.startsWith("matrix-")) return undefined;
  const bundleId = Object.keys(KOREAN_PACK_METADATA).find(
    (candidate) =>
      normalized === `matrix-${candidate}` ||
      normalized.startsWith(`matrix-${candidate}-`),
  );
  if (!bundleId) return undefined;
  const metadata = KOREAN_PACK_METADATA[bundleId];
  const pair = normalized.match(
    /-(ru|en|zh|ja|es|fr|de|ar|hi|ur|id|pt|it|ko|tr)-(ru|en|zh|ja|es|fr|de|ar|hi|ur|id|pt|it|ko|tr)$/,
  );
  return pair
    ? { ...metadata, name: `${metadata.name}: ${pair[1].toUpperCase()} → ${pair[2].toUpperCase()}` }
    : metadata;
}
